import { customEvent } from "goi_common";
import { GOI_PHONE, GOI_PHONE_HREF } from "@/constants/contact";
import { PhoneIcon } from "@/components/PhoneIcon";
import { useParlorInquiryModal } from "@/hooks/useParlorInquiryModal";

interface ParlorHeroProps {
  companyName: string;
  sidoName: string;
  sigunguName: string;
  managedType: string;
  binsoCount: number;
  address: string;
  parlorId: string;
}

export const ParlorHero = ({
  companyName,
  sidoName,
  sigunguName,
  managedType,
  binsoCount,
  address,
  parlorId,
}: ParlorHeroProps) => {
  const { openParlorInquiryModal } = useParlorInquiryModal("prlr_fam_main");
  const regionLabel = `${sidoName} · ${sigunguName}`;
  const subtitle = address.split(" ").slice(0, 4).join(" ");

  return (
    <section className="pd-hero" aria-labelledby="pd-page-title">
      <div className="pd-hero__head">
        <div className="pd-hero__tags">
          <span className="pd-tag pd-tag--region">{regionLabel}</span>
          <span className="pd-tag pd-tag--type">{managedType} 장례식장</span>
          {binsoCount > 0 && (
            <span className="pd-tag pd-tag--rooms">빈소 {binsoCount}실</span>
          )}
        </div>

        <h1 id="pd-page-title">{companyName}</h1>
        <p className="pd-hero__subtitle">{subtitle}</p>

        <p className="pd-hero__lede">
          {sidoName} {sigunguName}에 자리한 <b>{managedType} 장례식장</b>입니다.{" "}
          {binsoCount > 0 && `빈소 ${binsoCount}실을 갖추고 있으며, `}
          <b>빈소 정보·예상 비용 견적·장례 절차</b>를 고이장례연구소에서 무료로
          안내해 드립니다.
        </p>

        <dl className="pd-hero__keys" aria-label="기본 정보 요약">
          <div className="pd-key">
            <dt className="pd-key__label">시설 형태</dt>
            <dd className="pd-key__value">{managedType}</dd>
          </div>
          <div className="pd-key">
            <dt className="pd-key__label">빈소</dt>
            <dd className="pd-key__value">
              {binsoCount > 0 ? `${binsoCount}실` : "정보 없음"}
            </dd>
          </div>
          <div className="pd-key">
            <dt className="pd-key__label">지역</dt>
            <dd className="pd-key__value">{sigunguName}</dd>
          </div>
          <div className="pd-key">
            <dt className="pd-key__label">상담</dt>
            <dd className="pd-key__value">24시간</dd>
          </div>
        </dl>

        <div className="pd-hero__actions">
          <a
            className="pd-btn pd-btn--primary"
            href={GOI_PHONE_HREF}
            onClick={(event) => {
              event.preventDefault();
              customEvent({
                newGtm: {
                  path: "prlr_fam_main",
                  trigger_and_target: "btn-goi_call",
                },
              });
              void openParlorInquiryModal();
            }}
          >
            <PhoneIcon />
            무료 장례 상담
          </a>
          <a
            className="pd-btn pd-btn--ghost"
            href={`${process.env.NEXT_PUBLIC_PLATFORM_WEB_URL}/match/parlor-requests/${parlorId}/?utm_campaign=parlor`}
            onClick={() =>
              customEvent({
                newGtm: {
                  path: "prlr_fam_main",
                  trigger_and_target: "btn-uv-htch_rqst_prlr",
                },
              })
            }
          >
            시설 사용료 자세히 알아보기
          </a>
        </div>
        <span className="pd-hero__actions-note">
          e하늘 장사정보시스템 기반 고이장례연구소 제공
        </span>
      </div>

      <aside className="pd-hero__side" aria-labelledby="pd-quote-title">
        <span className="pd-side-eyebrow">고이장례연구소 무료 견적</span>
        <h2 id="pd-quote-title">
          <>
            빈소 가격 및<br />
            <em>장례 전체 비용 안내</em> 받기
          </>
        </h2>
        <p>
          장례식장 사용료, 차량, 용품, 인력까지 — 항목 단위로 투명한 견적을
          고이장례연구소에서 즉시 안내해 드립니다. 한 번뿐인 장례, 지금 꼭
          5분이라도 상담 받아보세요.
        </p>

        <div className="pd-hero__data-sources">
          <span className="pd-hero__data-badge">e하늘 장사정보시스템</span>
          <span className="pd-hero__data-badge">
            고이장례연구소 자체 데이터
          </span>
        </div>

        <div className="pd-tel-block">
          <div>
            <a
              href="tel:1666-9784"
              onClick={(event) => {
                event.preventDefault();
                customEvent({
                  newGtm: {
                    path: "prlr_fam_main",
                    trigger_and_target: "goi_call",
                  },
                });
                void openParlorInquiryModal();
              }}
            >
              <div className="pd-tel-block__label">
                24시간 무료 상담 (번호 클릭)
              </div>
              <div className="pd-tel-block__num">
                <PhoneIcon size={20} style={{ flexShrink: 0 }} />
                {GOI_PHONE}
              </div>
            </a>
          </div>
          <div className="pd-tel-block__stats">
            <div className="pd-tel-block__hint">22,000명+ 상담</div>
            <div className="pd-tel-block__hint pd-tel-block__hint--sub">
              후기 평균 4.9점
            </div>
          </div>
        </div>

        <a
          className="pd-side-cta"
          href={`${process.env.NEXT_PUBLIC_PLATFORM_WEB_URL}/match/parlor-requests/${parlorId}/?utm_campaign=parlor`}
          onClick={() =>
            customEvent({
              newGtm: {
                path: "prlr_fam_main",
                trigger_and_target: "uv-mtch_rqst_prlr",
              },
            })
          }
        >
          장례 절차 및 비용 알아보기 →
        </a>
        <p className="pd-side-disclaimer">
          e하늘 장사정보시스템을 기준으로 고이장례연구소에서 안내드립니다
        </p>

        <div className="pd-side-trust">
          <span>상담 무료</span>
          <span>후기 평균 4.9점</span>
          <span>1,900건+</span>
        </div>
      </aside>
    </section>
  );
};
